import './AvailableBotsForSitWithClub.styles.scss';

import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { Tooltip } from 'primereact/tooltip';
import React, { FC, useCallback, useEffect, useState } from 'react';

import { REFRESH_BOTS_FOR_SEAT_WITH_CLUBS_INTERVAL } from '../../constants';
import { AvailableBotResponse } from '../../model/availablebot/AvailableBotResponse';
import availableBotStore from '../../store/AvailableBotStore';
import notificationStore from '../../store/NotificationStore';
import Bot from '../Bot/Bot';
import { AvailableBotsForSitWithClubProps } from './AvailableBotsForSitWithClub.types';

const AvailableBotsForSitWithClub: FC<AvailableBotsForSitWithClubProps> = ({
  tableId,
  selectedClubs,
  excludedPids,
}) => {
  const [availableBotsForClub, setAvailableBotsForCLub] = useState<
    AvailableBotResponse[]
  >([]);
  const [selectedClub, setSelectedClub] = useState<string | null>(null);
  const [selectedBot, setSelectedBot] = useState<AvailableBotResponse | null>(
    null,
  );
  const [isDialogVisible, setDialogVisible] = useState<boolean>(false);

  const fetchAvailableBotsWithClubs = (): void => {
    if (selectedClubs?.length > 0) {
      availableBotStore
        .getAvailableBotsWithClubs(selectedClubs)
        .then(availableBots => {
          let filteredBots = availableBots.slice();
          filteredBots = filteredBots.filter(
            bot =>
              !excludedPids.includes(bot.pid) &&
              (!bot.tournamentTableIds ||
                !tableId ||
                !bot.tournamentTableIds.includes(tableId)),
          );
          setAvailableBotsForCLub(filteredBots);
        });
    } else {
      setAvailableBotsForCLub([]);
    }
  };

  useEffect(() => {
    fetchAvailableBotsWithClubs();
    const interval = setInterval(
      fetchAvailableBotsWithClubs,
      REFRESH_BOTS_FOR_SEAT_WITH_CLUBS_INTERVAL,
    );
    return () => clearInterval(interval);
  }, [selectedClubs, excludedPids]);

  const updateAvailableBotClub = useCallback(
    (availableBot: AvailableBotResponse, club: string): void => {
      if (club?.length > 0) {
        availableBotStore
          .updateClub(availableBot.pid, availableBot.room, club)
          .then(() =>
            notificationStore.showNotification({
              severity: 'success',
              summary: 'Success',
              detail: (
                <>
                  Request sent to the bot (<b>{availableBot.pid}</b>) to change
                  the club to <b>{club}</b>
                </>
              ),
            }),
          )
          .catch(() =>
            notificationStore.showNotification({
              severity: 'error',
              summary: 'Error',
              detail: (
                <>
                  Error while sending request to the bot (
                  <b>{availableBot.club}</b>) to change the club to{' '}
                  <b>{club}</b>
                </>
              ),
            }),
          );
      }
      setSelectedBot(null);
    },
    [],
  );

  const onChangeAvailableBotClub = useCallback(
    (availableBot: AvailableBotResponse): void => {
      if (selectedClubs.length > 0) {
        if (selectedClubs.length === 1) {
          updateAvailableBotClub(availableBot, selectedClubs[0]);
        } else {
          setSelectedBot(availableBot);
          setDialogVisible(true);
        }
      }
    },
    [selectedClubs.length, selectedClubs, updateAvailableBotClub],
  );

  const confirmMessage = (
    <div className="select-club-message">
      <Dropdown
        className="select-club"
        value={selectedClub}
        onChange={e => setSelectedClub(e.value)}
        options={selectedClubs.filter(club =>
          selectedBot?.availableClubs.includes(club),
        )}
        optionLabel="Select club"
      />
    </div>
  );

  return (
    <div className="AvailableBotsForSitWithClub-Component">
      <Tooltip
        target=".available-bot-with-clubs"
        appendTo={() => document.body}
      />
      {availableBotsForClub.map(availableBot => (
        <div
          key={`${availableBot.pid}-available-bot-with-clubs`}
          className="available-bot-with-clubs"
          data-pr-tooltip="Change club"
          data-pr-position="top"
        >
          <Bot
            bot={availableBot}
            selected={false}
            onSelectBot={onChangeAvailableBotClub}
          />
        </div>
      ))}
      <ConfirmDialog
        className="select-club-dialog"
        contentClassName="w-100"
        visible={isDialogVisible}
        onHide={() => setDialogVisible(false)}
        message={confirmMessage}
        header="Select club"
        acceptLabel="Confirm"
        rejectLabel="Cancel"
        accept={() => updateAvailableBotClub(selectedBot, selectedClub)}
      />
    </div>
  );
};

export default AvailableBotsForSitWithClub;
